// Toaster: grabs the ball, warms it and shoots it back up
import { defineObj } from '../core/registry';
import { block, rr, lg, shadowOn, shadowOff, C, DEG } from '../core/draw-helpers';
import type { Ctx2D, GameBody } from '../core/types';

export interface Toaster { x: number; y: number; sens: GameBody; pop: number[]; held: GameBody | null; until: number; cool: number; pops: number }
declare module '../core/types' { interface SimState { toasters: Toaster[] } }

export function drawToaster(ctx: Ctx2D, T: { x: number; y: number; held?: unknown }, t: number) {
  const { x, y } = T;
  ctx.save();
  shadowOn(ctx, 16, 8);
  rr(ctx, x - 78, y - 40, 156, 84, 26); ctx.fillStyle = lg(ctx, y - 40, y + 44, '#FFC4D6', '#F07FA6'); ctx.fill(); shadowOff(ctx);
  rr(ctx, x - 70, y - 34, 140, 14, 7); ctx.fillStyle = 'rgba(255,255,255,.5)'; ctx.fill();
  // slot
  rr(ctx, x - 24, y - 44, 48, 16, 6); ctx.fillStyle = '#5C3B55'; ctx.fill();
  if (T.held) { ctx.fillStyle = `rgba(255,${120 + Math.sin(t * 0.4) * 40},60,.8)`; rr(ctx, x - 20, y - 42, 40, 10, 5); ctx.fill(); }
  // handle
  const ly = T.held ? y + 18 : y - 16;
  rr(ctx, x + 70, ly, 22, 12, 6); ctx.fillStyle = C.ink; ctx.fill();
  // feet
  ctx.fillStyle = C.ink; rr(ctx, x - 64, y + 40, 18, 8, 4); ctx.fill(); rr(ctx, x + 46, y + 40, 18, 8, 4); ctx.fill();
  // button face (little smile)
  ctx.strokeStyle = 'rgba(92,59,85,.6)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.arc(x, y + 12, 12, 0.2 * Math.PI, 0.8 * Math.PI); ctx.stroke();
  ctx.fillStyle = 'rgba(92,59,85,.6)'; [-14, 14].forEach(s => { ctx.beginPath(); ctx.arc(x + s, y + 2, 3, 0, 7); ctx.fill(); });
  ctx.restore();
}
/** The funnel above the toaster (o.funnel) */
export function drawFunnel(ctx: Ctx2D, b: GameBody) { block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#E6E1F0', '#A9A3C0', 5); }

const isBall = (b: GameBody) => b.kind === 'hero' || b.kind === 'helper';

export const toaster = defineObj({
  phase: 'core', layer: 'mid', z: -1, thumb: 6,
  create(api, o) {
    const { add, R } = api, x = o.x, y = o.y, gap = 44;
    const TL = add(R(x - 22 - 28, y, 56, 80, { isStatic: true, chamfer: { radius: 10 } }));
    const TR = add(R(x + 22 + 28, y, 56, 80, { isStatic: true, chamfer: { radius: 10 } }));
    const B = add(R(x, y + 30, gap, 20, { isStatic: true }));
    TL.kind = TR.kind = B.kind = 'toasterpart';
    if (o.funnel) {
      const f1 = add(R(x - 56, y - 62, 80, 10, { isStatic: true, angle: 40 * DEG, chamfer: { radius: 4 } }));
      const f2 = add(R(x + 56, y - 62, 80, 10, { isStatic: true, angle: -40 * DEG, chamfer: { radius: 4 } }));
      f1.kind = f2.kind = 'funnel';
    }
    const sens = add(R(x, y + 5, gap - 6, 30, { isStatic: true, isSensor: true }));
    sens.kind = 'toastersens';
    const T: Toaster = { x, y, sens, pop: (o.pop as number[]) || [0, -17], held: null, until: 0, cool: 0, pops: 0 };
    api.S.toasters.push(T);
    return T;
  },
  draw: (ctx, st, t) => drawToaster(ctx, st, t),
  holds: (st, b) => st.held === b,
  blockers: o => [{ x: o.x - 40, y: o.y, r: 45 }, { x: o.x + 40, y: o.y, r: 45 }, { x: o.x, y: o.y - 40, r: 40 }],
  art: { funnel: { layer: 'cache', draw: drawFunnel } },
  system: {
    init: api => { api.S.toasters = []; },
    contact: {
      toastersens(api, x, y) {
        const S = api.S;
        if (!isBall(y)) return;
        const T = S.toasters.find(k => k.sens === x);
        if (T && !T.held && T.cool <= S.t) { T.held = y; T.until = S.t + 45; api.Body.setStatic(y, true); api.Body.setPosition(y, { x: T.x, y: T.y }); S.events.push({ e: 'toast', x: T.x, y: T.y }); }
      },
    },
    preStep(api) {
      const S = api.S, { Body } = api;
      for (const T of S.toasters) {
        if (T.held && S.t >= T.until) {
          const b = T.held; T.held = null; T.cool = S.t + 40; T.pops++;
          Body.setStatic(b, false); Body.setPosition(b, { x: T.x, y: T.y - 58 });
          Body.setVelocity(b, { x: T.pop[0], y: T.pop[1] });
          S.events.push({ e: 'pop', x: T.x, y: T.y });
        }
      }
    },
    busy: api => api.S.toasters.some(T => !!T.held),
  },
  intro(x, t, g, { fakeBall }) {
    const T = { x: 230, y: 196, held: t > 40 && t < 110 };
    let by;
    if (t < 40) by = 20 + 0.1 * t * t; else if (t < 110) by = 170; else { const k = (t - 110) / 70; by = 150 - 150 * Math.sin(Math.min(1, k) * Math.PI * 0.6); }
    if (!(t > 40 && t < 110)) g.ball(x, fakeBall(230 + (t > 110 ? (t - 110) * 1.5 : 0), by, 0, t < 40 ? 6 : -6), 'hero', t);
    drawToaster(x, T, t);
    if (t > 110 && t < 130) { x.fillStyle = '#FFC93C'; for (let i = 0; i < 6; i++) { x.beginPath(); x.arc(230 + Math.cos(i) * 40, 130 + Math.sin(i * 2) * 20, 4, 0, 7); x.fill(); } }
  },
});
